import { Link as ReactRouterLink } from 'react-router-dom';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import { DealerProspect } from '../../../models/dealer-prospect.model';

export interface ProspectsTableProps {
  leads: DealerProspect[];
  getCopyUrl: (row: DealerProspect) => string;
  onCopyLink: (url: string) => void;
  /** When true, show Archive/Unarchive actions (Dealer Prospects only). */
  showArchiveActions?: boolean;
  canArchive?: (user: any) => boolean;
  currentUser?: any;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
}

export default function ProspectsTable({
  leads,
  getCopyUrl,
  onCopyLink,
  showArchiveActions = false,
  canArchive,
  currentUser,
  onArchive,
  onUnarchive,
}: ProspectsTableProps) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell> </TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Dealer Name</TableCell>
            <TableCell>Date Created</TableCell>
            <TableCell>Created by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leads.map((row) => (
            <ProspectsTableRow
              key={row.id}
              row={row}
              getCopyUrl={getCopyUrl}
              onCopyLink={onCopyLink}
              showArchiveActions={showArchiveActions}
              canArchive={canArchive}
              currentUser={currentUser}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface ProspectsTableRowProps {
  row: DealerProspect;
  getCopyUrl: (row: DealerProspect) => string;
  onCopyLink: (url: string) => void;
  showArchiveActions: boolean;
  canArchive?: (user: any) => boolean;
  currentUser?: any;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
}

function ProspectsTableRow({
  row,
  getCopyUrl,
  onCopyLink,
  showArchiveActions,
  canArchive,
  currentUser,
  onArchive,
  onUnarchive,
}: ProspectsTableRowProps) {
  const copyUrl = getCopyUrl(row);
  const showArchive =
    showArchiveActions &&
    currentUser &&
    canArchive?.(currentUser) &&
    onArchive &&
    onUnarchive;

  return (
    <TableRow selected={row.isArchived}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Copy form link">
            <IconButton
              size="small"
              onClick={() => onCopyLink(copyUrl)}
              aria-label="Copy prospect form link"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View">
            <ReactRouterLink to={`/dealer-prospects/${row.id}`}>
              <VisibilityIcon color="primary" fontSize="small" />
            </ReactRouterLink>
          </Tooltip>
          {showArchive && !row.isArchived && (
            <Tooltip title="Archive">
              <ReactRouterLink onClick={() => onArchive?.(row.id)} to="">
                <ArchiveIcon color="error" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          )}
          {showArchive && row.isArchived && (
            <Tooltip title="Unarchive">
              <ReactRouterLink onClick={() => onUnarchive?.(row.id)} to="">
                <UnarchiveIcon fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>{row.notes || row.leadTypeName || '—'}</TableCell>
      <TableCell>{row.dealershipName || '—'}</TableCell>
      <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : '—'}</TableCell>
      <TableCell>{row.teamMemberName || '—'}</TableCell>
    </TableRow>
  );
}
